import React, { ReactElement, useState, useEffect, useMemo } from 'react';
import DrawerV6 from '@Common/Drawer/DrawerV6/DrawerV6';
import InputText from '@Common/InputText';
import { OVERVIEW_SECTIONS, OVERVIEW_SECTIONS_HELP_URL } from '@Constants/constants';
import { ProjectOption } from '../Overview';
import DualListSelector, { DualListItem } from './DualListSelector';
import { Tab } from '../components/types';
import './ConfigDrawer.scss';

interface ConfigDrawerProps {
    tab: Tab;
    availableProjects: Array<ProjectOption>;
    onSave: (tabId: number, tabLabel: string, selectedProjectIds: Array<number>, selectedSectionIds: Array<string>) => Promise<void>;
    onClose: () => void;
    /**
     * True when this drawer is creating a brand-new tab (the "+ New Tab" button) rather than
     * editing `tab`, which is a synthetic placeholder (blank label/selections) in that case.
     * Creation requires a name and at least one section — editing keeps its existing, looser rules.
     */
    isCreateMode?: boolean;
}

const ConfigDrawer = ({ tab, availableProjects, onSave, onClose, isCreateMode = false }: ConfigDrawerProps): ReactElement => {
    const [tabLabel, setTabLabel] = useState<string>(tab.label);
    const [selectedProjectIds, setSelectedProjectIds] = useState<Array<number | string>>(tab.selectedProjectIds ?? []);
    const [selectedSectionIds, setSelectedSectionIds] = useState<Array<number | string>>(tab.selectedSectionIds ?? []);

    // Re-initialize when the tab changes (drawer re-opens with a fresh tab)
    useEffect(() => {
        setTabLabel(tab.label);
        setSelectedProjectIds(tab.selectedProjectIds ?? []);
        setSelectedSectionIds(tab.selectedSectionIds ?? []);
    }, [tab]);

    const projectItems: DualListItem[] = useMemo(
        () => availableProjects.map((p) => ({ id: p.id, name: p.name })),
        [availableProjects]
    );

    const sectionItems: DualListItem[] = useMemo(
        () => OVERVIEW_SECTIONS.map((s) => ({ id: s.id, name: s.label })),
        []
    );

    const handleSave = async (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        await onSave(
            tab.id,
            tabLabel,
            selectedProjectIds.map((id) => Number(id)),
            selectedSectionIds.map((id) => String(id))
        );
    };

    const seeExamplesLink = (
        <a
            href={OVERVIEW_SECTIONS_HELP_URL}
            target='_blank'
            rel='noopener noreferrer'
            className='see-examples-link'>
            See Examples
        </a>
    );

    const isSaveDisabled = isCreateMode && (tabLabel.trim() === '' || selectedProjectIds.length === 0 || selectedSectionIds.length === 0);

    return (
        <DrawerV6
            isWide={true}
            title={isCreateMode ? 'New Tab' : `${tab.label} Tab Configuration`}
            onSaveClick={handleSave}
            saveText='Save'
            isSaveDisabled={isSaveDisabled}
            onCancelClick={onClose}>
            <div className='config-drawer'>
                <div className='row mb-3'>
                    <div className='col-8'>
                        <div className='form-group'>
                            <label htmlFor='tab-label' className='title'>
                                Tab Label
                            </label>
                            <InputText
                                name='tab-label'
                                type='text'
                                maxLength={20}
                                hintText='Character Limit 20'
                                value={tabLabel}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTabLabel(e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                <div className='row mb-2'>
                    <div className='col-12'>
                        <h6 className='section-heading'>Projects to display in this tab</h6>
                    </div>
                </div>
                <DualListSelector
                    available={projectItems}
                    selectedIds={selectedProjectIds}
                    onChange={setSelectedProjectIds}
                    virtualized={true}
                />

                <div className='row mt-4 mb-2'>
                    <div className='col-12 d-flex align-items-center justify-content-between'>
                        <h6 className='section-heading mb-0'>Sections to display in this tab</h6>
                        {seeExamplesLink}
                    </div>
                </div>
                <DualListSelector
                    available={sectionItems}
                    selectedIds={selectedSectionIds}
                    onChange={setSelectedSectionIds}
                    virtualized={false}
                />
            </div>
        </DrawerV6>
    );
};

export default ConfigDrawer;
